
import { useState, useMemo } from "react";
import { Card, Badge, Button, ErrorBoundary, Tabs, Tab, Pagination } from "@weretradeit/ui-react";
import { researchArticles, type ResearchArticle } from "../data/researchArticles";

const ARTICLES_PER_PAGE = 5;

const ResearchWidgetContent = () => {
  const [selectedArticle, setSelectedArticle] = useState<ResearchArticle | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);

  // Extract unique categories from all articles
  const categories = useMemo(() => {
    const cats = new Set<string>();
    researchArticles.forEach(article => cats.add(article.category));
    return Array.from(cats).sort();
  }, []);

  // Filter articles based on search and category
  const filteredArticles = useMemo(() => {
    return researchArticles.filter(article => {
      // Category filter
      if (selectedCategory !== "all" && article.category !== selectedCategory) {
        return false;
      }

      // Search filter (searches in title, summary, and tags)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = article.title.toLowerCase().includes(query);
        const matchesSummary = article.summary.toLowerCase().includes(query);
        const matchesTags = article.tags.some(tag => tag.toLowerCase().includes(query));
        return matchesTitle || matchesSummary || matchesTags;
      }

      return true;
    });
  }, [searchQuery, selectedCategory]);

  // Paginate filtered articles
  const totalPages = Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE);
  const paginatedArticles = useMemo(() => {
    const startIndex = (currentPage - 1) * ARTICLES_PER_PAGE;
    return filteredArticles.slice(startIndex, startIndex + ARTICLES_PER_PAGE);
  }, [filteredArticles, currentPage]);

  // Reset to page 1 when filters change
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  /* 0.6.x konnte null melden (Reiter abgewaehlt), 3.x meldet immer einen
     Schluessel. Der `|| "all"`-Rueckfall bleibt trotzdem stehen: er faengt
     auch den leeren String. */
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category || "all");
    setCurrentPage(1);
  };

  if (selectedArticle) {
    return (
      <Card>
        <div style={{ marginBottom: "16px" }}>
          <Button
            variant="primary"
            onClick={() => setSelectedArticle(null)}
            style={{ marginBottom: "16px" }}
            aria-label="Back to article list"
          >
            ← Back to Articles
          </Button>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <Badge tone="info">{selectedArticle.category}</Badge>
            <span style={{ color: "var(--text-muted)", fontSize: "14px" }}>{selectedArticle.date}</span>
          </div>
          <h2 style={{ marginTop: 0, marginBottom: "12px" }}>{selectedArticle.title}</h2>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "24px" }}>
            {selectedArticle.tags.map((tag) => (
              <Badge key={tag}>{tag}</Badge>
            ))}
          </div>
        </div>
        <div
          className="research-content"
          style={{
            lineHeight: "1.6",
            maxWidth: "900px",
          }}
          dangerouslySetInnerHTML={{
            __html: formatMarkdown(selectedArticle.content)
          }}
        />
      </Card>
    );
  }

  return (
    <Card title="Research Articles">
      <p style={{ marginBottom: "16px", color: "var(--text-muted)" }}>
        In-depth research and analysis on AI, Web3, blockchain, and technology trends.
      </p>

      {/* Search Bar */}
      <div style={{ marginBottom: "20px" }}>
        <input
          type="search"
          placeholder="Search articles by title, content, or tags..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pill"
          style={{ width: "100%", maxWidth: "600px" }}
          aria-label="Search research articles"
        />
        {searchQuery && (
          <p style={{ marginTop: "8px", fontSize: "14px", color: "var(--text-muted)" }}>
            Found {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Category Tabs */}
      {categories.length > 1 && (
        <div style={{ marginBottom: "24px" }}>
          {/* `id` kennt TabsProps nicht — 3.x vergibt die Kennungen der
              Reiter selbst und bindet sie ueber aria-controls. onSelect gibt
              jetzt immer einen Schluessel, nie null. */}
          <Tabs
            activeKey={selectedCategory}
            onSelect={handleCategoryChange}
          >
            <Tab eventKey="all" title={`All (${researchArticles.length})`}>
              <div />
            </Tab>
            {categories.map(category => {
              const count = researchArticles.filter(a => a.category === category).length;
              return (
                <Tab
                  key={category}
                  eventKey={category}
                  title={`${category} (${count})`}
                >
                  <div />
                </Tab>
              );
            })}
          </Tabs>
        </div>
      )}

      {/* Articles List */}
      {paginatedArticles.length === 0 ? (
        <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--text-muted)" }}>
          <p style={{ fontSize: "16px", marginBottom: "8px" }}>No articles found</p>
          <p style={{ fontSize: "14px" }}>Try adjusting your search or filter</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {paginatedArticles.map((article) => (
            <Card
              key={article.id}
              className="component-box"
              style={{ cursor: "pointer" }}
              role="button"
              tabIndex={0}
              aria-label={`Read article: ${article.title}`}
              onClick={() => setSelectedArticle(article)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedArticle(article);
                }
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <Badge tone="info">{article.category}</Badge>
                <span style={{ color: "var(--text-muted)", fontSize: "14px" }}>{article.date}</span>
              </div>
              <strong style={{ display: "block", marginBottom: "8px", fontSize: "18px" }}>
                {article.title}
              </strong>
              <p style={{ margin: 0, marginBottom: "12px", color: "var(--text-default)" }}>
                {article.summary}
              </p>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {article.tags.map((tag) => (
                  <Badge key={tag}>{tag}</Badge>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ marginTop: "24px" }}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            maxVisible={5}
          />
        </div>
      )}
    </Card>
  );
};

// Simple markdown-to-HTML converter
function formatMarkdown(markdown: string): string {
  let html = markdown;

  // Headers (order matters: h4 -> h3 -> h2 -> h1 to avoid partial matches)
  html = html.replace(/^#### (.*$)/gim, '<h4 style="margin-top: 24px; margin-bottom: 10px; color: var(--text-default); font-size: 1.1em;">$1</h4>');
  html = html.replace(/^### (.*$)/gim, '<h3 style="margin-top: 32px; margin-bottom: 12px; color: var(--text-default);">$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2 style="margin-top: 40px; margin-bottom: 16px; color: var(--text-default); border-bottom: 2px solid var(--border-default); padding-bottom: 8px;">$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1 style="margin-top: 0; margin-bottom: 24px; color: var(--text-default);">$1</h1>');

  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Lists
  html = html.replace(/^- (.*$)/gim, '<li style="margin-left: 20px;">$1</li>');
  html = html.replace(/^(\d+)\. (.*$)/gim, '<li style="margin-left: 20px;">$2</li>');

  // Wrap consecutive <li> in <ul>
  html = html.replace(/(<li.*?>.*?<\/li>\n?)+/g, (match) => `<ul style="margin: 16px 0;">${match}</ul>`);

  // Code blocks
  html = html.replace(/```([\s\S]*?)```/g, '<pre style="background: var(--bg-subtle); padding: 16px; border-radius: var(--radius-sm); overflow-x: auto; margin: 16px 0;"><code>$1</code></pre>');

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code style="background: var(--bg-subtle); padding: 2px 6px; border-radius: 3px; font-family: monospace;">$1</code>');

  // Images (NEW: Must come before links to avoid conflict)
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width: 100%; height: auto; border-radius: 8px; margin: 16px 0; display: block;" />');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: var(--color-primary); text-decoration: none;" target="_blank" rel="noopener noreferrer">$1</a>');

  // Horizontal rules
  html = html.replace(/^---$/gim, '<hr style="border: none; border-top: 1px solid var(--border-default); margin: 32px 0;" />');

  // Tables - basic support
  html = html.replace(/\n\|(.+)\|\n\|[-:|]+\|\n((?:\|.+\|\n)+)/g, (match, header, rows) => {
    const headerCells = header.split('|').filter(Boolean).map((cell: string) =>
      `<th style="text-align: left; padding: 12px; border-bottom: 2px solid var(--border-default);">${cell.trim()}</th>`
    ).join('');

    const rowsHtml = rows.trim().split('\n').map((row: string) => {
      const cells = row.split('|').filter(Boolean).map((cell: string) =>
        `<td style="padding: 12px; border-bottom: 1px solid var(--border-subtle);">${cell.trim()}</td>`
      ).join('');
      return `<tr>${cells}</tr>`;
    }).join('');

    return `<table style="width: 100%; border-collapse: collapse; margin: 24px 0; font-size: 14px;"><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table>`;
  });

  // Paragraphs (preserve spacing)
  html = html.replace(/\n\n/g, '</p><p style="margin: 16px 0;">');
  html = `<p style="margin: 16px 0;">${html}</p>`;

  // Checkmarks and X marks
  html = html.replace(/✅/g, '<span style="color: #22c55e;">✅</span>');
  html = html.replace(/❌/g, '<span style="color: #ef4444;">❌</span>');
  html = html.replace(/⚠️/g, '<span style="color: #f59e0b;">⚠️</span>');

  return html;
}

// Export with ErrorBoundary wrapper to prevent crashes from propagating
export const ResearchWidget = () => (
  /* `fallback` ist in 3.x eine FUNKTION (error, reset) => ReactNode, kein
     fertiges Element. Als Element uebergeben rief React es nie auf — der
     Rueckfall waere im Fehlerfall gar nicht erschienen. Der reset-Knopf ist
     neu: bisher blieb nur „Seite neu laden". */
  <ErrorBoundary
    fallback={(_error, reset) => (
      <Card title="Error Loading Research">
        <p>An error occurred while loading the research articles.</p>
        <Button variant="secondary" onClick={reset}>Erneut versuchen</Button>
      </Card>
    )}
  >
    <ResearchWidgetContent />
  </ErrorBoundary>
);
